---
title: "Publishing jobs to your careers page"
description: "How jobs appear on your Chosen careers page: publishing the page, what job status controls, taking a job down safely, and your careers page URL."
category: "careers"
keywords:
  - "publish job posting"
  - "careers page visibility"
  - "unpublish job ATS"
  - "job posting URL"
schema: "HowTo"
nav:
  section: "guides"
  order: 10
  parent: "/guides/careers"
---

Two things decide whether the public sees a job: your careers page has to be published, and the job's status has to be **Open**. Publish the page once, then use each job's status to decide what's listed.

## The careers page is off until you turn it on

A brand-new org has no public careers page. Click **Career Page** at the bottom of the left sidebar. Under **Page Status**, switch it from **Hidden** to **Published** and save.

While the page is Hidden, the URL returns a 404, for the careers page *and* for every job link under it. Open jobs stay invisible to the public until the page itself is on.

You only do this once. After that, the page stays on and each job's status is all that matters.

<Callout type="info" title="Preview the page before you publish it">
Career Page settings has a **Preview URL** that works whether the page is Hidden or Published. Use it to check the page looks right before you flip it public. Anyone with the link can see the preview, so don't treat it as private.
</Callout>

## Open jobs are listed

A job's status decides where it shows up:

- **Open**: on your careers page, with its own URL, taking applications.
- **Internal**: hidden from the public, but works like any open job inside Chosen.
- **Closed**: off the careers page and not taking applications.

New jobs start **Open**, so with your page published a new job is public as soon as you create it. If it isn't ready yet, pick **Internal** in the **Status** field before you save, and switch it to Open when it is.

A listed job is reachable two ways: from the careers page job list, where candidates filter by department, location, and remote policy, and by its direct link. See [embedding jobs elsewhere](/guides/careers/embed-and-api) for sharing that link.

To reach further than your own page, turn on [posting to free job boards](/guides/careers/job-boards) for the job. That's a separate switch, off by default.

## Take a job down, don't delete it

When you're done hiring for a role, click **Close position** in the job's edit dialog, or set its status to Internal. The job leaves the careers page and stops accepting new applications.

Closing does **not** remove the people who already applied. They stay in the job's candidate list and in your reporting. Reopen the same job later and pick up where you left off.

<Callout type="warning" title="Deleting a job also deletes its candidates">
Closing and deleting are not the same. Deleting a job removes everyone who applied to it along with the posting, which is usually not what you want. If a role is done, close it. Save delete for jobs created by mistake that never collected a real applicant.
</Callout>

## Your URL slug

Your careers page lives at `jobs.chosenhq.com/careers/<your-org-slug>`. Set the slug in Career Page settings, in the **Career Page Slug** field: lowercase letters, numbers, and hyphens only.

Change it before you've shared the link widely. When you change the slug, every old `/careers/<old-slug>` link stops working: job descriptions, social posts, email signatures, job board listings, all of it. Pick one early and leave it alone.

## How Chosen tracks where applicants came from

Every applicant gets a **Source** recorded automatically: the careers page, a referral, a reply to [sourcing outreach](/guides/sourcing/outreach-sending), and so on.

Two things worth knowing:

- **Tagged links are tracked.** Add `?utm_source=` to links you post elsewhere (`?utm_source=newsletter`) and applicants who arrive through them get the Source "Career page · newsletter". Job boards tag their links for you, so their applicants show the board's name.
- **Source is editable.** If a candidate mentions your CTO referred them, correct the Source field on their candidate record. The automatic value is a starting point, not the last word.

## Limits

Status is all-or-nothing per job: a job is listed or it isn't. There's no scheduled publish date and no per-job audience. To stage a launch, keep the job Internal until you're ready, then set it to Open: it joins the careers page list and its [direct link](/guides/careers/embed-and-api) starts working at the same moment.
